import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { preloadHighlighter } from "@pierre/diffs";
import { routeTree } from "./routeTree.gen";
import { getThreadSyncManager } from "@/lib/thread-sync-manager";
import { installStaleDeploymentRecovery } from "@/lib/stale-deployment-recovery";
import "@/lib/analytics";
import "./index.css";

// Pierre's shared highlighter is initialized with ??=, so this preload must run
// before any diff component mounts. We intentionally preload no languages here:
// Pierre can lazily resolve languages by filename, while this still locks the
// highlighter onto Shiki's WASM Oniguruma engine instead of the slow JS regex engine.
if (typeof window !== "undefined") {
  void preloadHighlighter({
    preferredHighlighter: "shiki-wasm",
    themes: ["github-dark"],
    langs: [],
  });
}

installStaleDeploymentRecovery();

// Match the global query stale window so intent preloads can satisfy immediate
// navigation instead of becoming stale before the route renders.
const ROUTER_PRELOAD_STALE_TIME_MS = 60_000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreloadStaleTime: ROUTER_PRELOAD_STALE_TIME_MS,
});

getThreadSyncManager().setQueryClient(queryClient);

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

// Service worker registration is handled by the useSwUpdate() hook
// in __root.tsx, which also manages update detection and auto-reload.
